import React, { useState, useEffect, useCallback, useRef } from 'react'
import { RefreshCcw, Search, Clock, CheckCircle, XCircle, AlertTriangle, FileText, Shield, Eye, ScanLine, Play, Trash2, Pencil, Smartphone } from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'
import ImageModal from '../../components/ImageModal'
import ProfileAmlMatchReviewPanel from '../../components/aml/ProfileAmlMatchReviewPanel'

const STATUS_CONFIG = {
  OCR_CAPTURE: { label: 'OCR only (pre-DVS)', color: 'teal', icon: ScanLine },
  NSW_DDL_CAPTURE: { label: 'NSW digital licence', color: 'blue', icon: Smartphone },
  DRAFT: { label: 'Draft', color: 'gray', icon: FileText },
  DOCUMENTS_PENDING: { label: 'Documents Pending', color: 'yellow', icon: FileText },
  DVS_PENDING: { label: 'DVS Pending', color: 'blue', icon: Shield },
  DVS_PROCESSING: { label: 'DVS Processing', color: 'blue', icon: Shield },
  DVS_FAILED: { label: 'DVS Failed', color: 'red', icon: XCircle },
  AML_PENDING: { label: 'AML Pending', color: 'purple', icon: Shield },
  AML_PROCESSING: { label: 'AML Processing', color: 'purple', icon: Shield },
  MANUAL_REVIEW: { label: 'Manual Review Required', color: 'orange', icon: AlertTriangle },
  COMPLETED: { label: 'Completed', color: 'green', icon: CheckCircle },
  REJECTED: { label: 'Rejected', color: 'red', icon: XCircle }
}

const FILTER_OPTIONS = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'PENDING', label: 'All Pending' },
  { value: 'DVS_PENDING', label: 'DVS Pending' },
  { value: 'DVS_FAILED', label: 'DVS Failed' },
  { value: 'AML_PENDING', label: 'AML Pending' },
  { value: 'MANUAL_REVIEW', label: 'Manual Review' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'REJECTED', label: 'Rejected' }
]

const EMPTY_STATS = {
  total: 0,
  dvsPending: 0,
  dvsFailed: 0,
  amlPending: 0,
  amlFailed: 0,
  manualReview: 0,
  completed: 0,
  ocrCaptures: 0,
  nswDdlCaptures: 0
}

/** ProfileSafe profileID from API, ocr_data, or OCR row (used for GreenID ref). */
function resolveProfileSafeId(item) {
  if (!item) return null
  const direct =
    item.profilesafeProfileId ||
    item.ocrData?.profilesafe_profile_id ||
    item.ocrData?.profile_id ||
    item.ocrData?.profileID ||
    item.patronSnapshot?.profileID ||
    item.patronSnapshot?.profilesafe_profile_id ||
    null
  if (direct && String(direct).trim()) return String(direct).trim()
  if (item.rowKind === 'OCR' && item.masterProfileId && !String(item.masterProfileId).startsWith('ocr_')) {
    return item.masterProfileId
  }
  return null
}

function formatProfileSafeIdShort(id) {
  if (!id) return '—'
  const s = String(id)
  if (s.length <= 16) return s
  return `${s.slice(0, 8)}…${s.slice(-6)}`
}

/** @returns {'ok'|'err'|'busy'|'warn'|'pending'} */
function classifyDvsTone(item) {
  const s = String(item?.dvsStatus || 'PENDING').toUpperCase().replace(/^DVS_/, '')
  if (s === 'VERIFIED' || item?.dvsResult?.verified === true) return 'ok'
  if (s.includes('FAIL') || item?.dvsResult?.verified === false) return 'err'
  if (s.includes('PROCESS') || s.includes('PROGRESS')) return 'busy'
  return 'pending'
}

/** @returns {'ok'|'err'|'busy'|'warn'|'pending'} */
function classifyAmlTone(item) {
  const s = String(item?.amlStatus || 'PENDING').toUpperCase().replace(/^AML_/, '')
  const onboarding = String(item?.onboardingStatus || '').toUpperCase()
  if (s === 'CLEAR') return 'ok'
  if (s.includes('MATCH') || s.includes('HIT') || s.includes('FAIL')) return 'err'
  if (s.includes('PROCESS')) return 'busy'
  // Stale queue label after screening cleared with no hits
  if (s === 'PENDING_REVIEW' && onboarding === 'COMPLETED') return 'ok'
  if (s === 'PENDING_REVIEW') return 'warn'
  return 'pending'
}

function verificationToneMeta(tone) {
  switch (tone) {
    case 'ok':
      return {
        Icon: CheckCircle,
        chip: 'bg-green-100 text-green-800 border-green-200',
        tabActive: 'border-green-600 text-green-800 bg-green-50',
        labelClass: 'text-green-700',
      }
    case 'err':
      return {
        Icon: XCircle,
        chip: 'bg-red-100 text-red-800 border-red-200',
        tabActive: 'border-red-600 text-red-800 bg-red-50',
        labelClass: 'text-red-700',
      }
    case 'busy':
      return {
        Icon: RefreshCcw,
        chip: 'bg-blue-100 text-blue-800 border-blue-200',
        tabActive: 'border-blue-600 text-blue-800 bg-blue-50',
        labelClass: 'text-blue-700',
      }
    case 'warn':
      return {
        Icon: AlertTriangle,
        chip: 'bg-amber-100 text-amber-900 border-amber-200',
        tabActive: 'border-amber-600 text-amber-900 bg-amber-50',
        labelClass: 'text-amber-800',
      }
    default:
      return {
        Icon: Clock,
        chip: 'bg-gray-100 text-gray-700 border-gray-200',
        tabActive: 'border-psg-teal text-psg-teal bg-teal-50',
        labelClass: 'text-gray-600',
      }
  }
}

function formatDvsStatusLabel(item) {
  const s = String(item?.dvsStatus || 'PENDING').toUpperCase().replace(/^DVS_/, '')
  if (s === 'VERIFIED') return 'Verified'
  if (s.includes('FAIL')) return 'Failed'
  if (s.includes('PROCESS')) return 'Processing'
  return s === 'PENDING' ? 'Pending' : s
}

function isDvsVerified(item) {
  const s = String(item?.dvsStatus || '').toUpperCase().replace(/^DVS_/, '')
  return s === 'VERIFIED' || item?.dvsResult?.verified === true
}

/** Edit / re-run DVS only while not already verified. */
function canEditOrRerunDvs(item) {
  if (!item || item.rowKind === 'OCR' || item.rowKind === 'NSW_DDL') return false
  return !isDvsVerified(item)
}

function formatAmlStatusLabel(item) {
  const s = String(item?.amlStatus || 'PENDING').toUpperCase().replace(/^AML_/, '')
  if (s === 'CLEAR') return 'Clear'
  if (s === 'PENDING_REVIEW') return 'Needs review'
  if (s.includes('MATCH') || s.includes('HIT')) return 'Match'
  if (s.includes('PROCESS')) return 'Processing'
  if (s.includes('FAIL')) return 'Failed'
  return s === 'PENDING' ? 'Pending' : s
}

function verificationTabStatusPill(tone, label, active) {
  const meta = verificationToneMeta(tone)
  const StatusIcon = meta.Icon
  if (active) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold text-white">
        <StatusIcon className={`w-3.5 h-3.5 ${tone === 'busy' ? 'animate-spin' : ''}`} />
        {label}
      </span>
    )
  }
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${meta.chip}`}>
      <StatusIcon className={`w-3.5 h-3.5 ${tone === 'busy' ? 'animate-spin' : ''}`} />
      {label}
    </span>
  )
}

/**
 * Operator-facing DVS failure text — drop GBG test-licence boilerplate; keep status
 * and real GreenID hints (e.g. "Given name does not match.").
 */
function shortenDvsFailureMessage(raw) {
  if (raw == null) return ''
  let s = String(raw).trim()
  if (!s) return ''
  s = s
    .replace(/\s*(?:GBG\s+)?test NSW:[\s\S]*$/i, '')
    .replace(/\s*—?\s*check licence and card numbers\.?/gi, '')
    .replace(/\s*—\s*$/g, '')
    .trim()

  const statusLine = s.match(
    /^(?:document verification status|greenID status|DVS status)\s*:\s*([A-Z0-9_]+)\s*(?:[—\-–:]\s*(.*))?$/i
  )
  if (statusLine) {
    const status = statusLine[1].toUpperCase()
    const rest = (statusLine[2] || '').trim()
    if (!rest || /^check\b/i.test(rest)) return status
    return rest.length <= 160 ? rest : status
  }

  const pending = s.match(/^DVS not verified\s*\(([A-Z0-9_]+)\)(?:\s*:\s*(.*))?$/i)
  if (pending) {
    const status = pending[1].toUpperCase()
    const rest = (pending[2] || '').trim()
    if (!rest || /^check\b/i.test(rest)) return status
    return rest.length <= 160 ? rest : status
  }

  const failed = s.match(/^DVS failed\s*\(([A-Z0-9_]+)\)(?:\s*:\s*(.*))?$/i)
  if (failed) {
    const status = failed[1].toUpperCase()
    const rest = (failed[2] || '').trim()
    if (!rest) return status
    return rest.length <= 160 ? rest : status
  }

  return s
}

function dvsResponseSummary(item) {
  if (!item) return '—'
  const r = item.dvsResult
  if (r?.verified === true) return 'Verified'
  const raw =
    r?.failure_reason ||
    item.errorMessage ||
    r?.message ||
    r?.error ||
    null
  if (raw) return shortenDvsFailureMessage(raw) || 'Not verified'
  if (!r) {
    if (item.dvsCheckedAt) return 'No stored response payload'
    return 'Not run yet'
  }
  return r.verified === false ? 'Not verified' : 'See full response'
}

function DvsResponsePanel({ item, formFields }) {
  const [showRaw, setShowRaw] = React.useState(false)
  if (!item || item.rowKind === 'OCR') return null
  const summary = dvsResponseSummary(item)
  const shortError = item.errorMessage ? shortenDvsFailureMessage(item.errorMessage) : ''
  const hasPayload = item.dvsResult || item.errorMessage || item.ocrData || item.rowKind === 'PIPELINE'
  const documentDataForJson = dvsFieldsForDocumentType(
    formFields && Object.keys(formFields).length
      ? { ...effectiveDvsFields(item), ...formFields }
      : effectiveDvsFields(item)
  )

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium text-gray-900">DVS response</div>
          <p className="text-gray-700 mt-1 break-words">{summary}</p>
        </div>
        {item.dvsCheckedAt && (
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {new Date(item.dvsCheckedAt).toLocaleString()}
          </span>
        )}
      </div>
      {item.retryCount > 0 && (
        <p className="text-xs text-gray-500 mt-1">Attempts recorded: {item.retryCount}</p>
      )}
      {hasPayload && (
        <>
          <button
            type="button"
            className="text-xs text-psg-teal mt-2 underline"
            onClick={() => setShowRaw((v) => !v)}
          >
            {showRaw ? 'Hide' : 'Show'} full JSON
          </button>
          {showRaw && (
            <div className="mt-2 space-y-2">
              <div>
                <div className="text-xs font-medium text-gray-600 mb-1">
                  DVS document data ({documentDataForJson.document_type})
                </div>
                <pre className="text-xs bg-white border rounded p-2 overflow-auto max-h-40 whitespace-pre-wrap">
                  {JSON.stringify(documentDataForJson, null, 2)}
                </pre>
              </div>
              {shortError && (
                <div>
                  <div className="text-xs font-medium text-red-700 mb-1">Error</div>
                  <pre className="text-xs bg-white border border-red-100 rounded p-2 overflow-auto max-h-32 whitespace-pre-wrap text-red-900">
                    {shortError}
                  </pre>
                </div>
              )}
              {item.dvsResult && (
                <div>
                  <div className="text-xs font-medium text-gray-600 mb-1">Provider result</div>
                  <pre className="text-xs bg-white border rounded p-2 overflow-auto max-h-64 whitespace-pre-wrap">
                    {JSON.stringify(item.dvsResult, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

/** GBG greenID DVS document types (aligned with `GreenIdV6Client.sourceIdForDocument`). */
const GBG_DVS_DOCUMENT_TYPES = [
  {
    value: 'drivers_licence',
    label: "Driver's licence",
    numberLabel: 'Licence number',
    showState: true,
    showCardNumber: true,
    showCountry: false,
    showExpiry: false,
    showMedicareExtra: false,
  },
  {
    value: 'proof_of_age',
    label: 'Proof of age',
    numberLabel: 'Document number',
    showState: true,
    showCardNumber: true,
    showCountry: false,
    showExpiry: false,
    showMedicareExtra: false,
  },
  {
    value: 'passport',
    label: 'Passport',
    numberLabel: 'Passport number',
    showState: false,
    showCardNumber: false,
    showCountry: true,
    showExpiry: true,
    showMedicareExtra: false,
  },
  {
    value: 'medicare',
    label: 'Medicare',
    numberLabel: 'Medicare number',
    showState: false,
    showCardNumber: false,
    showCountry: false,
    showExpiry: true,
    showMedicareExtra: true,
  },
]

function normalizeGbgDocumentType(documentType) {
  const dt = String(documentType || '')
    .toLowerCase()
    .replace(/-/g, '_')
  if (dt === 'driver_licence' || dt === 'drivers_license' || dt === 'driver_license') {
    return 'drivers_licence'
  }
  if (GBG_DVS_DOCUMENT_TYPES.some((t) => t.value === dt)) return dt
  return 'drivers_licence'
}

function dvsDocTypeConfig(documentType) {
  return (
    GBG_DVS_DOCUMENT_TYPES.find((t) => t.value === normalizeGbgDocumentType(documentType)) ||
    GBG_DVS_DOCUMENT_TYPES[0]
  )
}

/** Type-scoped DVS fields for display / retry preview (omit irrelevant keys). */
function dvsFieldsForDocumentType(fields) {
  const f = fields || {}
  const cfg = dvsDocTypeConfig(f.document_type)
  const out = {
    document_type: normalizeGbgDocumentType(f.document_type),
    document_number: f.document_number || '',
    given_name: f.given_name || '',
    middle_name: f.middle_name || '',
    family_name: f.family_name || '',
    date_of_birth: f.date_of_birth || '',
  }
  if (cfg.showState) out.state_of_issue = f.state_of_issue || ''
  if (cfg.showCountry) out.country_code = f.country_code || 'AU'
  if (cfg.showCardNumber) out.document_card_number = f.document_card_number || ''
  if (cfg.showExpiry) out.expiry_date = f.expiry_date || ''
  if (cfg.showMedicareExtra) {
    out.medicare_reference = f.medicare_reference || ''
    out.medicare_colour = f.medicare_colour || 'G'
  }
  if (f.ocr_source) out.ocr_source = f.ocr_source
  return out
}

function needsLicenceCardNumber(itemOrFields) {
  const ocr = itemOrFields?.ocrData || itemOrFields || {}
  const dt = String(ocr.document_type || '').toLowerCase().replace(/-/g, '_')
  const cc = String(ocr.country_code || 'AU').trim().toUpperCase()
  return (dt === 'drivers_licence' || dt === 'proof_of_age') && (cc === 'AU' || cc === 'AUS')
}

function ocrLicenceCardNumber(ocr) {
  if (!ocr) return ''
  return String(
    ocr.document_card_number || ocr.licence_card_number || ocr.rta_number || ''
  ).trim()
}

function formatDobDisplay(val) {
  if (!val) return '—'
  const s = String(val)
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`
  return s.slice(0, 32)
}

/** Merged view: ocr_data JSON + queue row name/DOB columns. */
function effectiveDvsFields(item) {
  if (!item) return {}
  const ocr = item.ocrData || {}
  const rawType =
    ocr.document_type || (item.payoutId || item.documentUrls ? 'drivers_licence' : '')
  return {
    document_type: rawType ? normalizeGbgDocumentType(rawType) : 'drivers_licence',
    document_number: ocr.document_number || '',
    document_card_number: ocrLicenceCardNumber(ocr),
    given_name: ocr.given_name || item.firstName || '',
    middle_name: ocr.middle_name || ocr.middleName || item.middleName || '',
    family_name: ocr.family_name || item.lastName || '',
    date_of_birth: ocr.date_of_birth || (item.dateOfBirth ? String(item.dateOfBirth).slice(0, 10) : ''),
    state_of_issue: ocr.state_of_issue || 'NSW',
    country_code: ocr.country_code || 'AU',
    expiry_date: ocr.expiry_date ? String(ocr.expiry_date).slice(0, 10) : '',
    medicare_reference: ocr.medicare_reference || ocr.individual_reference_number || '',
    medicare_colour: ocr.medicare_colour || 'G',
    ocr_source: ocr.ocr_source || null,
  }
}

function isPayoutPipelineRow(item) {
  return !!(item?.payoutId && item.rowKind !== 'OCR')
}

function formatDvsProviderForUi(provider) {
  if (provider == null || provider === '') return '—'
  const u = String(provider).toUpperCase()
  if (u === 'GREENID' || u === 'GREEN_ID') return 'DVS'
  return String(provider)
}

const DOC_SLOTS = [
  { key: 'id_front', label: 'ID front' },
  { key: 'id_back', label: 'ID back' },
  { key: 'patron_photo', label: 'Patron photo' },
]

function QueueDocumentImage({ queueId, slot, label }) {
  const [src, setSrc] = React.useState(null)
  const [failed, setFailed] = React.useState(false)
  const [previewOpen, setPreviewOpen] = React.useState(false)

  React.useEffect(() => {
    let revoked = null
    setSrc(null)
    setFailed(false)
    setPreviewOpen(false)
    const url = getLocalApiUrl(
      `/api/profiles/verification-queue/${encodeURIComponent(queueId)}/document/${slot}`
    )
    authFetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.blob()
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob)
        revoked = objectUrl
        setSrc(objectUrl)
      })
      .catch(() => setFailed(true))
    return () => {
      if (revoked) URL.revokeObjectURL(revoked)
    }
  }, [queueId, slot])

  return (
    <div className="border rounded-lg overflow-hidden bg-gray-50">
      <div className="text-xs font-medium text-gray-600 px-2 py-1 border-b bg-white flex items-center justify-between gap-2">
        <span>{label}</span>
        {src ? <span className="text-[10px] font-normal text-psg-teal">Click to enlarge</span> : null}
      </div>
      <div className="min-h-[120px] flex items-center justify-center p-2">
        {src ? (
          <button
            type="button"
            className="group relative w-full rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-psg-teal"
            onClick={(e) => {
              e.stopPropagation()
              setPreviewOpen(true)
            }}
            title={`View larger ${label}`}
          >
            <img
              src={src}
              alt={label}
              className="max-h-48 w-full object-contain rounded cursor-zoom-in transition group-hover:opacity-90"
            />
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition bg-black/20 rounded">
              <Eye className="w-6 h-6 text-white drop-shadow" />
            </span>
          </button>
        ) : failed ? (
          <span className="text-xs text-gray-500">Not available</span>
        ) : (
          <span className="text-xs text-gray-400">Loading…</span>
        )}
      </div>
      <ImageModal
        isOpen={previewOpen && !!src}
        onClose={() => setPreviewOpen(false)}
        imageSrc={src}
        imageAlt={label}
        title={label}
      />
    </div>
  )
}

/** NSW DDL portrait — load via auth API (private blob URLs break as raw <img src>). */
function NswDdlPortraitImage({ itemId, fallbackUrl }) {
  const [src, setSrc] = React.useState(null)
  const [failed, setFailed] = React.useState(false)
  const [failReason, setFailReason] = React.useState(null)
  const [previewOpen, setPreviewOpen] = React.useState(false)

  React.useEffect(() => {
    let revoked = null
    let cancelled = false
    setSrc(null)
    setFailed(false)
    setFailReason(null)
    setPreviewOpen(false)

    const applyBlob = (blob) => {
      if (cancelled || !blob) return
      const objectUrl = URL.createObjectURL(blob)
      revoked = objectUrl
      setSrc(objectUrl)
    }

    const load = async () => {
      // Signed Azure URLs work as <img src> without fetch/CORS
      if (fallbackUrl && /[?&]sig=/i.test(fallbackUrl)) {
        if (!cancelled) setSrc(fallbackUrl)
        return
      }

      if (itemId) {
        try {
          const url = getLocalApiUrl(
            `/api/profiles/verification-queue/${encodeURIComponent(itemId)}/document/patron_photo`
          )
          const res = await authFetch(url)
          if (res.ok) {
            applyBlob(await res.blob())
            return
          }
          const body = await res.json().catch(() => ({}))
          if (!cancelled) {
            setFailReason(body?.error || `HTTP ${res.status}`)
          }
        } catch {
          /* fall through */
        }
      }
      if (!cancelled) setFailed(true)
    }

    load()
    return () => {
      cancelled = true
      if (revoked) URL.revokeObjectURL(revoked)
    }
  }, [itemId, fallbackUrl])

  return (
    <div className="border rounded-lg p-4">
      <h4 className="font-semibold text-gray-900 mb-2 flex items-center justify-between gap-2">
        <span>Licence portrait</span>
        {src ? <span className="text-[10px] font-normal text-psg-teal">Click to enlarge</span> : null}
      </h4>
      <div className="min-h-[160px] flex items-center justify-center rounded bg-gray-50 px-3">
        {src ? (
          <button
            type="button"
            className="group relative w-full rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-psg-teal"
            onClick={() => setPreviewOpen(true)}
            title="View larger licence portrait"
          >
            <img
              src={src}
              alt="NSW digital licence portrait"
              className="max-h-64 w-full object-contain rounded cursor-zoom-in transition group-hover:opacity-90"
              onError={() => {
                setSrc(null)
                setFailed(true)
                setFailReason('Image URL could not be loaded')
              }}
            />
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition bg-black/20 rounded">
              <Eye className="w-6 h-6 text-white drop-shadow" />
            </span>
          </button>
        ) : failed ? (
          <div className="text-center text-sm text-gray-600 space-y-1 py-4">
            <div className="font-medium text-gray-700">Portrait not available</div>
            <div className="text-xs text-gray-500 max-w-md mx-auto">
              {failReason ||
                'Not removed by DVS retention — file could not be read from blob storage (check Azure blob connection string).'}
            </div>
          </div>
        ) : (
          <span className="text-sm text-gray-400">Loading portrait…</span>
        )}
      </div>
      <ImageModal
        isOpen={previewOpen && !!src}
        onClose={() => setPreviewOpen(false)}
        imageSrc={src}
        imageAlt="NSW digital licence portrait"
        title="Licence portrait"
      />
    </div>
  )
}

/** Compact one-line retention alert for Verification Queue document gallery. */
function ImageRetentionBanner({ profile }) {
  const r = profile?.imageRetention
  const dvsDone = isDvsVerified(profile)
  const policy = String(r?.policy || '').toLowerCase()

  let message = null
  let tone = 'border-gray-200 bg-gray-50 text-gray-700'

  if (r?.state === 'deleted' || (dvsDone && policy !== 'always_keep')) {
    message = 'Images removed as per configuration'
    tone = 'border-teal-200 bg-teal-50 text-teal-900'
  } else if (r?.state === 'scheduled') {
    message = 'Images: scheduled for removal as per configuration'
    tone = 'border-blue-200 bg-blue-50 text-blue-900'
  } else if (policy === 'always_keep' || (dvsDone && r?.imagesPresent && policy === 'always_keep')) {
    message = 'Images retained as per configuration'
  } else if (r?.imagesPresent || r?.state === 'retained') {
    if (policy === 'never_keep') {
      message = 'Images: remove after DVS (as configured)'
    } else {
      message = 'Images: retain if DVS fails · remove on DVS success'
    }
  } else if (r && r.state !== 'none' && r.state !== 'unknown') {
    message = 'Images retained as per configuration'
  }

  if (!message) return null

  return (
    <div
      className={`mt-2 rounded-md border px-2.5 py-1.5 text-xs leading-snug ${tone}`}
      role="status"
    >
      {message}
    </div>
  )
}

/** Format DVS request name so Given / Middle / Family splits are visible in attempt history. */
function formatDvsAttemptNameParts(req = {}) {
  const given = String(req.given_name || '').trim()
  const middle = String(req.middle_name || '').trim()
  const family = String(req.family_name || '').trim()
  const parts = []
  if (given) parts.push(`Given: ${given}`)
  if (middle) parts.push(`Middle: ${middle}`)
  if (family) parts.push(`Family: ${family}`)
  return parts.length ? parts.join(' · ') : null
}

function QueueDocumentGallery({ profile }) {
  const queueId = profile?.id
  const hasRefs =
    profile?.documentUrls &&
    typeof profile.documentUrls === 'object' &&
    DOC_SLOTS.some((s) => profile.documentUrls[s.key])
  const docCount = Number(profile?.documentsCount) || 0

  if (!queueId) return null
  return (
    <>
      <ImageRetentionBanner profile={profile} />
      {!hasRefs ? (
        String(profile?.dvsStatus || '').toUpperCase() === 'VERIFIED' ||
        profile?.imageRetention?.state === 'deleted' ? null : (
          <p className="text-xs text-gray-500 mt-1.5">
            {docCount > 0
              ? 'No ID images stored for this item (OCR fields only).'
              : 'No ID images on file.'}
          </p>
        )
      ) : (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {DOC_SLOTS.filter(({ key }) => profile.documentUrls[key]).map(({ key, label }) => (
            <QueueDocumentImage key={key} queueId={queueId} slot={key} label={label} />
          ))}
        </div>
      )}
    </>
  )
}

export default function VerificationQueue() {
  const { can } = useAuth()
  const canManage = can('profile-safe/checks', 'manage')

  const [items, setItems] = useState([])
  const [ocrItems, setOcrItems] = useState([])
  const [nswDdlItems, setNswDdlItems] = useState([])
  const [ocrListIncluded, setOcrListIncluded] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedProfile, setSelectedProfile] = useState(null)
  const [processDvsLoading, setProcessDvsLoading] = useState(false)
  const [processDvsMessage, setProcessDvsMessage] = useState(null)
  const [clearLoading, setClearLoading] = useState(null)
  const [clearMessage, setClearMessage] = useState(null)
  const [retryingId, setRetryingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [runningAmlId, setRunningAmlId] = useState(null)
  const [dvsCardNumber, setDvsCardNumber] = useState('')
  const [dvsFields, setDvsFields] = useState({
    document_type: 'drivers_licence',
    document_number: '',
    document_card_number: '',
    given_name: '',
    middle_name: '',
    family_name: '',
    date_of_birth: '',
    state_of_issue: 'NSW',
    country_code: 'AU',
    expiry_date: '',
    medicare_reference: '',
    medicare_colour: 'G',
  })
  const [rowActionMessage, setRowActionMessage] = useState(null)
  const [detailTab, setDetailTab] = useState('dvs') // 'dvs' | 'aml'
  const [editingDvsFields, setEditingDvsFields] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [amlVerifyData, setAmlVerifyData] = useState(null)
  const amlReconcileAttemptRef = useRef('')
  const [amlVerifyLoading, setAmlVerifyLoading] = useState(false)
  const [dispositionRemarks, setDispositionRemarks] = useState('')
  const [dispositionSaving, setDispositionSaving] = useState(false)
  const [amlVerifySaving, setAmlVerifySaving] = useState(false)
  const [filters, setFilters] = useState({
    status: 'ALL',
    search: '',
    profileType: 'ALL'
  })
  const [stats, setStats] = useState({ ...EMPTY_STATS })
  const [listTab, setListTab] = useState('dvs')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 1,
    hasMore: false,
  })

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const params = new URLSearchParams()
      params.append('page', String(page))
      params.append('limit', String(pageSize))
      if (filters.status !== 'ALL') {
        if (filters.status === 'PENDING') {
          params.append('status', 'DVS_PENDING,DVS_PROCESSING,AML_PENDING,AML_PROCESSING')
        } else {
          params.append('status', filters.status)
        }
      }
      if (filters.profileType !== 'ALL') {
        params.append('profileType', filters.profileType)
      }
      if (filters.search) {
        params.append('search', filters.search)
      }

      try {
        const response = await authFetch(getLocalApiUrl(`/api/profiles/verification-queue?${params}`))
        const result = await response.json().catch(() => ({}))

        if (response.ok && result.success) {
          setItems(result.data || [])
          setOcrItems(result.ocrSubmissions || [])
          setNswDdlItems(result.nswDdlSubmissions || [])
          setOcrListIncluded(result.ocrListIncluded !== false)
          setStats({ ...EMPTY_STATS, ...(result.stats || {}) })
          const p = result.pagination || {}
          setPagination({
            page: Number(p.page) || page,
            limit: Number(p.limit) || pageSize,
            total: Number(p.total) || 0,
            totalPages: Math.max(1, Number(p.totalPages) || 1),
            hasMore: !!p.hasMore,
          })
          return
        }

        const msg =
          result.error ||
          result.message ||
          (response.status === 403
            ? 'You do not have permission to view the verification queue (requires profile-safe/checks: view).'
            : response.status
              ? `Could not load verification queue (HTTP ${response.status}).`
              : 'Could not load verification queue.')
        setError(msg)
        setItems([])
        setOcrItems([])
        setNswDdlItems([])
        setOcrListIncluded(false)
        setStats({ ...EMPTY_STATS })
        setPagination({ page: 1, limit: pageSize, total: 0, totalPages: 1, hasMore: false })
      } catch (apiError) {
        console.log('API not available:', apiError.message)
        setError(apiError.message || 'Could not reach the API.')
        setItems([])
        setOcrItems([])
        setNswDdlItems([])
        setOcrListIncluded(false)
        setStats({ ...EMPTY_STATS })
        setPagination({ page: 1, limit: pageSize, total: 0, totalPages: 1, hasMore: false })
      }

    } catch (err) {
      console.error('Load error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [filters, page, pageSize])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadData()
    }, 30000)
    return () => clearInterval(interval)
  }, [loadData])

  const totalPages = Math.max(1, pagination.totalPages || 1)
  const rangeFrom = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1
  const rangeTo = Math.min(pagination.page * pagination.limit, pagination.total)

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT
    const Icon = config.icon
    
    const colorClasses = {
      gray: 'bg-gray-100 text-gray-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      blue: 'bg-blue-100 text-blue-800',
      purple: 'bg-purple-100 text-purple-800',
      orange: 'bg-orange-100 text-orange-800',
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
      teal: 'bg-teal-100 text-teal-800'
    }

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${colorClasses[config.color]}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    )
  }

  const getProfileTypeBadge = (type) => {
    const badges = {
      MEMBER: { label: 'New Member', color: 'bg-blue-100 text-blue-800' },
      EXISTING_MEMBER: { label: 'Existing Member', color: 'bg-green-100 text-green-800' },
      VISITOR: { label: 'Visitor', color: 'bg-yellow-100 text-yellow-800' }
    }
    const badge = badges[type] || badges.MEMBER
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    )
  }

  const isNonActiveQueueItem = (item) => {
    if (item?.isNonActive === true) return true
    const ps = String(item?.profileStatus || '').trim().toUpperCase()
    if (ps && ps !== 'ACTIVE') return true
    const ms = String(item?.membershipStatus || '').trim().toLowerCase()
    return [
      'archived', 'lapsed', 'inactive', 'cancelled', 'canceled', 'expired',
      'suspended', 'deleted', 'closed', 'terminated', 'non-financial',
      'nonfinancial', 'unfinancial', 'resigned', 'rejected',
    ].includes(ms)
  }

  const nonActiveQueueLabel = (item) => {
    const ps = String(item?.profileStatus || '').trim().toUpperCase()
    if (ps === 'SUSPENDED') return 'Suspended — non-active member'
    if (ps === 'DELETED') return 'Deleted — non-active member'
    if (ps && ps !== 'ACTIVE') return 'Inactive — non-active member'
    const ms = String(item?.membershipStatus || '').trim()
    return ms ? `${ms} — non-active member` : 'Non-active member'
  }

  const getNonActiveBadge = (item) => {
    if (!isNonActiveQueueItem(item)) return null
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-200 text-amber-950 border border-amber-400"
        title={item.membershipStatus ? `Membership: ${item.membershipStatus}` : 'Patron is not active'}
      >
        {nonActiveQueueLabel(item)}
      </span>
    )
  }

  const getTimeSince = (timestamp) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp)) / 1000)
    if (seconds < 60) return `${seconds}s ago`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }

  const syncDvsFieldsFromItem = (item) => {
    const ef = effectiveDvsFields(item)
    setDvsCardNumber(ef.document_card_number || '')
    setDvsFields({
      document_type: ef.document_type || 'drivers_licence',
      document_number: ef.document_number || '',
      document_card_number: ef.document_card_number || '',
      given_name: ef.given_name || '',
      middle_name: ef.middle_name || '',
      family_name: ef.family_name || '',
      date_of_birth: ef.date_of_birth || '',
      state_of_issue: ef.state_of_issue || 'NSW',
      country_code: ef.country_code || 'AU',
      expiry_date: ef.expiry_date || '',
      medicare_reference: ef.medicare_reference || '',
      medicare_colour: ef.medicare_colour || 'G',
    })
  }

  const handleViewProfile = async (item) => {
    setSelectedProfile(item)
    syncDvsFieldsFromItem(item)
    setDetailTab('dvs')
    setEditingDvsFields(false)
    setAmlVerifyData(null)
    amlReconcileAttemptRef.current = ''
    setDispositionRemarks(item?.reviewRemarks || '')
    setRowActionMessage((prev) => (prev?.id === item?.id ? prev : null))
    if (!item?.id || item.rowKind === 'OCR') return
    try {
      setDetailLoading(true)
      const response = await authFetch(
        getLocalApiUrl(`/api/profiles/verification-queue/${encodeURIComponent(item.id)}`)
      )
      const result = await response.json().catch(() => ({}))
      if (response.ok && result.data) {
        mergeQueueItem(result.data)
        setSelectedProfile(result.data)
        syncDvsFieldsFromItem(result.data)
        setDispositionRemarks(result.data.reviewRemarks || '')
        if (needsManualVerify(result.data)) {
          loadAmlVerifyForItem(result.data)
        }
      }
    } catch {
      // keep list row if detail fetch fails
    } finally {
      setDetailLoading(false)
    }
  }

  useEffect(() => {
    if (selectedProfile?.id) syncDvsFieldsFromItem(selectedProfile)
  }, [selectedProfile?.id, selectedProfile?.ocrData, selectedProfile?.updatedAt])

  useEffect(() => {
    if (selectedProfile && isDvsVerified(selectedProfile)) setEditingDvsFields(false)
  }, [selectedProfile?.id, selectedProfile?.dvsStatus])

  const mergeQueueItem = (updated) => {
    if (!updated?.id) return
    setItems((prev) => prev.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)))
    setSelectedProfile((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev))
  }

  const queueItemHasName = (item) => {
    if (!item) return false
    const given = String(item.firstName || item.ocrData?.given_name || '').trim()
    const family = String(item.lastName || item.ocrData?.family_name || '').trim()
    return !!(given || family)
  }

  const canRunAmlForItem = (item) => {
    if (!item || item.rowKind === 'OCR' || item.rowKind === 'NSW_DDL') return false
    if (!queueItemHasName(item)) return false
    const aml = String(item.amlStatus || 'PENDING').toUpperCase().replace(/^AML_/, '')
    if (aml.includes('PROCESS')) return false
    if (aml === 'NOT_REQUIRED' || aml === 'CLEAR') return false
    if (aml.includes('FAIL')) return true
    if (aml.includes('MATCH') || aml.includes('HIT')) {
      const payload = amlPayloadForItem(item)
      const hitFlags = Boolean(payload?.pep_match || payload?.sanctions_match)
      const hitCount = Number(payload?.result_count) > 0
      return !hitFlags && !hitCount
    }
    return true
  }

  const amlPayloadForItem = (item) => {
    const raw = item?.dvsResult?.aml
    return raw && typeof raw === 'object' ? raw : null
  }

  const needsManualVerify = (item) => {
    if (!item || item.rowKind === 'OCR') return false
    const aml = String(item.amlStatus || '').toUpperCase().replace(/^AML_/, '')
    const onboarding = String(item.onboardingStatus || '').toUpperCase()
    const amlPayload = amlPayloadForItem(item)
    const hitFlags = Boolean(amlPayload?.pep_match || amlPayload?.sanctions_match)
    const hitCount = Number(amlPayload?.result_count) > 0

    if (aml.includes('MATCH') || aml.includes('HIT')) {
      if (!hitFlags && !hitCount) return false
      if (item.amlAnalystDecision === 'no_match' && !hitCount) return false
      return true
    }

    // Stale label: screening cleared but aml_status stuck on PENDING_REVIEW
    if (aml === 'PENDING_REVIEW' && onboarding === 'COMPLETED') return false

    if (aml === 'PENDING_REVIEW') {
      return hitFlags || hitCount
    }

    return false
  }

  const preferredAmlCheckIdForItem = (item, overrideId = null) => {
    if (overrideId) return overrideId
    const payload = amlPayloadForItem(item)
    const fromPayload = payload?.aml_check_id ?? payload?.amlCheckId
    if (fromPayload) return fromPayload
    return null
  }

  const queueRowIdFromItem = (item) => {
    const raw = String(item?.id || '').trim()
    if (!raw) return null
    return raw.startsWith('profile_') ? raw.slice('profile_'.length) : raw
  }

  /** Approve / Reject when DVS or AML did not auto-clear (or Manual Review hold). */
  const canShowDisposition = (item) => {
    if (!item || item.rowKind === 'OCR' || item.rowKind === 'NSW_DDL') return false
    const s = String(item.onboardingStatus || '').toUpperCase()
    return ['MANUAL_REVIEW', 'DVS_FAILED', 'AML_FAILED'].includes(s)
  }

  const submitDisposition = async (item, decision) => {
    if (!canManage || !item?.id || item.rowKind === 'OCR' || item.rowKind === 'NSW_DDL') return
    const remarks = String(dispositionRemarks || '').trim()
    if (!remarks) {
      setRowActionMessage({
        id: item.id,
        type: 'err',
        text: 'Add remarks explaining why you are approving or rejecting',
      })
      return
    }
    const onboardingStatus = decision === 'approve' ? 'COMPLETED' : 'REJECTED'
    const label = decision === 'approve' ? 'Approve' : 'Reject'
    if (
      !window.confirm(
        `${label} this profile?\n\nRemarks will be saved for audit:\n"${remarks}"`
      )
    ) {
      return
    }
    try {
      setDispositionSaving(true)
      setRowActionMessage(null)
      const response = await authFetch(
        getLocalApiUrl(`/api/profiles/verification-queue/${encodeURIComponent(item.id)}`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ onboardingStatus, remarks }),
        }
      )
      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || `${label} failed (HTTP ${response.status})`)
      }
      const savedStatus = String(result.data?.onboardingStatus || onboardingStatus).toUpperCase()
      if (savedStatus !== onboardingStatus) {
        throw new Error(
          `${label} did not stick (status is ${savedStatus || 'unknown'}). Apply migration 255 and retry.`
        )
      }
      if (result.data) {
        mergeQueueItem(result.data)
        setSelectedProfile(result.data)
      } else {
        mergeQueueItem({
          id: item.id,
          onboardingStatus,
          reviewRemarks: remarks,
        })
      }
      setDispositionRemarks('')
      // Show the item under Completed / Rejected after refresh (loadData follows via filters effect)
      setFilters((prev) => ({ ...prev, status: onboardingStatus }))
      setPage(1)
      setRowActionMessage({
        id: item.id,
        type: decision === 'approve' ? 'ok' : 'err',
        text:
          result.message ||
          (decision === 'approve'
            ? 'Approved — now under Completed (DVS result unchanged if it failed)'
            : 'Rejected with remarks'),
      })
    } catch (e) {
      setRowActionMessage({ id: item.id, type: 'err', text: e.message || `${label} failed` })
    } finally {
      setDispositionSaving(false)
    }
  }

  const amlMemberKeyForItem = (item) => {
    if (!item) return null
    const mid =
      item.masterProfileId ||
      item.memberId ||
      item.ocrData?.membership_number ||
      item.ocrData?.MembershipNumber ||
      null
    if (!mid || String(mid).startsWith('ocr_')) return null
    return String(mid).trim()
  }

  const reconcileAmlIfNoHits = useCallback(async (item) => {
    if (!canManage || !item?.id || item.rowKind === 'OCR') return null
    try {
      const response = await authFetch(
        getLocalApiUrl(
          `/api/profiles/verification-queue/${encodeURIComponent(item.id)}/reconcile-aml`
        ),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
      )
      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result?.success) return null
      if (result.data) {
        mergeQueueItem(result.data)
        setSelectedProfile((prev) => (prev?.id === result.data.id ? { ...prev, ...result.data } : prev))
      }
      if (result.reconciled) {
        setAmlVerifyData(null)
        setRowActionMessage({
          id: item.id,
          type: 'ok',
          kind: 'aml',
          text: 'AML cleared — no PEP/sanctions screening hits on record',
        })
      }
      return result
    } catch {
      return null
    }
  }, [canManage])

  const loadAmlVerifyForItem = useCallback(async (item, preferredCheckId = null) => {
    if (!item) {
      setAmlVerifyData(null)
      return
    }
    const memberKey = amlMemberKeyForItem(item)
    let checkId = preferredAmlCheckIdForItem(item, preferredCheckId)
    if (!checkId && !memberKey) {
      setAmlVerifyData(null)
      return
    }
    setAmlVerifyLoading(true)
    try {
      if (!checkId && memberKey) {
        const listResp = await authFetch(
          getLocalApiUrl(`/api/aml/checks?member_id=${encodeURIComponent(memberKey)}&limit=20`)
        )
        const listJson = await listResp.json().catch(() => ({}))
        const checks = Array.isArray(listJson?.data) ? listJson.data : []
        const queueRowId = queueRowIdFromItem(item)
        const withHits = checks.filter((c) => Number(c.result_count) > 0)
        const forQueue = queueRowId
          ? withHits.find((c) => {
              const doc = c.document_data
              const parsed =
                doc && typeof doc === 'string'
                  ? (() => {
                      try {
                        return JSON.parse(doc)
                      } catch {
                        return null
                      }
                    })()
                  : doc
              return String(parsed?.verification_queue_id || '') === String(queueRowId)
            })
          : null
        checkId =
          forQueue?.id ||
          withHits[0]?.id ||
          checks.find((c) => Number(c.result_count) > 0)?.id ||
          checks[0]?.id ||
          null
      }
      if (!checkId) {
        setAmlVerifyData({ checkId: null, results: [], loaded: true, empty: true })
        await reconcileAmlIfNoHits(item)
        return
      }
      const detailResp = await authFetch(
        getLocalApiUrl(`/api/aml/checks/${encodeURIComponent(String(checkId))}`)
      )
      const detailJson = await detailResp.json().catch(() => ({}))
      if (!detailResp.ok || !detailJson?.success || !detailJson.data) {
        setAmlVerifyData({ checkId, results: [], loaded: true, empty: true })
        await reconcileAmlIfNoHits(item)
        return
      }
      const d = detailJson.data
      const sample = Array.isArray(d.result_sample) ? d.result_sample : []
      const emptyNoHits =
        sample.length === 0 && !d.pep_match && !d.sanctions_match && Number(d.result_count || 0) === 0
      setAmlVerifyData({
        checkId: d.id,
        name:
          d.full_name ||
          `${item.firstName || ''} ${item.lastName || ''}`.trim(),
        results: sample,
        analystDecision: d.analyst_decision || null,
        analystReason: d.analyst_reason || '',
        resultCount: Number(d.result_count) || sample.length,
        loaded: true,
        empty: emptyNoHits,
        pepMatch: d.pep_match,
        sanctionsMatch: d.sanctions_match,
      })
      if (emptyNoHits) {
        await reconcileAmlIfNoHits(item)
      }
    } catch {
      setAmlVerifyData({ checkId: null, results: [], loaded: true, empty: true })
    } finally {
      setAmlVerifyLoading(false)
    }
  }, [reconcileAmlIfNoHits])

  const runAmlForItem = async (item) => {
    if (!canManage || !item?.id || item.rowKind === 'OCR') return
    try {
      setRunningAmlId(item.id)
      setRowActionMessage(null)
      const response = await authFetch(
        getLocalApiUrl(`/api/profiles/verification-queue/${encodeURIComponent(item.id)}/run-aml`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
      )
      const result = await response.json().catch(() => ({}))
      if (result.data) mergeQueueItem(result.data)
      if (!response.ok || result.success === false) {
        throw new Error(result.error || result.message || `AML run failed (HTTP ${response.status})`)
      }
      setDetailTab('aml')
      const amlClear = String(result.data?.amlStatus || '').toUpperCase() === 'CLEAR'
      setRowActionMessage({
        id: item.id,
        type: amlClear ? 'ok' : 'err',
        kind: 'aml',
        text: result.message || 'AML/PEP screening completed (GreenID watchlist)',
      })
      const checkId = result.aml?.amlCheckId || null
      if (!amlClear || checkId) {
        await loadAmlVerifyForItem(result.data || item, checkId)
      } else {
        setAmlVerifyData(null)
      }
    } catch (e) {
      setDetailTab('aml')
      setRowActionMessage({
        id: item.id,
        type: 'err',
        kind: 'aml',
        text: e.message || 'AML run failed',
      })
    } finally {
      setRunningAmlId(null)
    }
  }

  useEffect(() => {
    if (!selectedProfile?.id || detailTab !== 'aml') return

    const aml = String(selectedProfile.amlStatus || '').toUpperCase().replace(/^AML_/, '')
    const payload = amlPayloadForItem(selectedProfile)
    const payloadNoHits =
      payload &&
      !payload.pep_match &&
      !payload.sanctions_match &&
      Number(payload.result_count || 0) === 0

    if (['MATCH', 'PENDING_REVIEW'].includes(aml) && payloadNoHits) {
      const reconcileKey = `${selectedProfile.id}:${aml}:${payload?.result_count ?? 'x'}`
      if (amlReconcileAttemptRef.current !== reconcileKey) {
        amlReconcileAttemptRef.current = reconcileKey
        reconcileAmlIfNoHits(selectedProfile)
      }
      return
    }

    if (!needsManualVerify(selectedProfile)) {
      setAmlVerifyData(null)
      return
    }
    const preferredCheckId = preferredAmlCheckIdForItem(selectedProfile)
    if (
      amlVerifyData?.loaded &&
      amlVerifyData?.checkId &&
      String(amlVerifyData.checkId) === String(preferredCheckId || amlVerifyData.checkId)
    ) {
      return
    }
    loadAmlVerifyForItem(selectedProfile, preferredCheckId)
  }, [selectedProfile?.id, selectedProfile?.amlStatus, selectedProfile?.dvsResult, detailTab]) // eslint-disable-line react-hooks/exhaustive-deps

  const runDvsForItem = async (item) => {
    if (!canManage || !item?.id || item.rowKind === 'OCR') return
    if (!canEditOrRerunDvs(item)) return
    const docCfg = dvsDocTypeConfig(dvsFields.document_type)
    const ef = {
      ...dvsFields,
      document_card_number: dvsFields.document_card_number || dvsCardNumber,
      country_code: dvsFields.country_code || 'AU',
    }
    const card = String(ef.document_card_number || '').trim()
    if (!ef.document_number?.trim() || !ef.given_name?.trim() || !ef.family_name?.trim()) {
      setRowActionMessage({
        id: item.id,
        type: 'err',
        text: `Fill ${docCfg.numberLabel.toLowerCase()}, given name, and family name in DVS document data (detail panel), then Run DVS.`,
      })
      if (!selectedProfile || selectedProfile.id !== item.id) handleViewProfile(item)
      return
    }
    if (needsLicenceCardNumber({ ...item, ocrData: ef }) && !card) {
      setRowActionMessage({
        id: item.id,
        type: 'err',
        text:
          'Enter card number (required by GBG for AU driver licence / proof of age DVS — separate from licence number), then Run DVS. GBG test: card 1111111111, licence 11111111.',
      })
      if (!selectedProfile || selectedProfile.id !== item.id) handleViewProfile(item)
      return
    }
    try {
      setRetryingId(item.id)
      setRowActionMessage(null)
      const retryBody = {
        document_type: ef.document_type,
        document_number: ef.document_number,
        document_card_number: docCfg.showCardNumber ? card || undefined : undefined,
        given_name: ef.given_name,
        middle_name: ef.middle_name != null ? ef.middle_name : '',
        family_name: ef.family_name,
        // Keep multi-word given/middle/family exactly as typed on this form
        exact_name_fields: true,
        date_of_birth: ef.date_of_birth || undefined,
        state_of_issue: docCfg.showState ? ef.state_of_issue : undefined,
        country_code: ef.country_code || 'AU',
        expiry_date: docCfg.showExpiry ? ef.expiry_date || undefined : undefined,
        medicare_reference: docCfg.showMedicareExtra ? ef.medicare_reference || undefined : undefined,
        medicare_colour: docCfg.showMedicareExtra ? ef.medicare_colour || undefined : undefined,
      }
      const response = await authFetch(
        getLocalApiUrl(`/api/profiles/verification-queue/${encodeURIComponent(item.id)}/retry-dvs`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(retryBody),
        }
      )
      const result = await response.json().catch(() => ({}))
      if (result.data) {
        mergeQueueItem(result.data)
        setSelectedProfile((prev) =>
          prev && prev.id === result.data.id ? { ...prev, ...result.data } : prev
        )
      }
      if (!response.ok || result.success === false) {
        const errRaw =
          result.data?.dvsResult?.failure_reason ||
          result.data?.errorMessage ||
          result.error ||
          result.message ||
          `DVS run failed (HTTP ${response.status})`
        throw new Error(shortenDvsFailureMessage(errRaw) || errRaw)
      }
      const verified =
        result.verified === true ||
        result.data?.dvsStatus === 'VERIFIED' ||
        result.data?.dvsResult?.verified === true
      const failReason = shortenDvsFailureMessage(
        result.data?.dvsResult?.failure_reason ||
          result.data?.errorMessage ||
          result.message
      )
      setDetailTab('dvs')
      setEditingDvsFields(false)
      setRowActionMessage({
        id: item.id,
        type: verified ? 'ok' : 'err',
        kind: 'dvs',
        text: verified
          ? result.message || 'DVS verified'
          : failReason || 'DVS did not verify — try alternate name spelling',
      })
      if (result.data) {
        mergeQueueItem(result.data)
        setSelectedProfile((prev) =>
          prev && prev.id === result.data.id ? { ...prev, ...result.data } : result.data
        )
        syncDvsFieldsFromItem(result.data)
      } else if (!selectedProfile || selectedProfile.id !== item.id) {
        handleViewProfile(item)
      }
    } catch (e) {
      setDetailTab('dvs')
      setRowActionMessage({
        id: item.id,
        type: 'err',
        kind: 'dvs',
        text: shortenDvsFailureMessage(e.message) || e.message || 'DVS run failed',
      })
      if (!selectedProfile || selectedProfile.id !== item.id) handleViewProfile(item)
    } finally {
      setRetryingId(null)
    }
  }

  const confirmClearPhrase = (label) => {
    if (!window.confirm(`Delete ALL ${label} for this tenant? This cannot be undone.`)) return null
    const typed = window.prompt('Type CLEAR to confirm:')
    if (typed !== 'CLEAR') {
      setClearMessage('Clear cancelled — you must type CLEAR exactly.')
      return null
    }
    return 'CLEAR'
  }

  const softDeleteQueueItem = async (item) => {
    if (!canManage || !item?.id) return
    if (item.rowKind === 'NSW_DDL') {
      window.alert('NSW digital licence captures are kept for audit and cannot be removed from this list.')
      return
    }
    const label =
      item.rowKind === 'OCR'
        ? `OCR capture for ${item.firstName || ''} ${item.lastName || ''}`.trim()
        : `queue entry for ${item.firstName || ''} ${item.lastName || ''} (${item.masterProfileId || item.id})`.trim()
    const ok = window.confirm(
      `Remove ${label} from the verification queue?\n\nThis is a soft delete — the row is kept for audit but hidden from the list.`
    )
    if (!ok) return
    try {
      setDeletingId(item.id)
      setRowActionMessage(null)
      const response = await authFetch(
        getLocalApiUrl(`/api/profiles/verification-queue/${encodeURIComponent(item.id)}`),
        { method: 'DELETE' }
      )
      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || `Delete failed (HTTP ${response.status})`)
      }
      setItems((prev) => prev.filter((i) => i.id !== item.id))
      setOcrItems((prev) => prev.filter((i) => i.id !== item.id))
      if (selectedProfile?.id === item.id) setSelectedProfile(null)
      setRowActionMessage({ id: item.id, type: 'ok', text: 'Removed from queue' })
      await loadData()
    } catch (e) {
      setRowActionMessage({ id: item.id, type: 'err', text: e.message || 'Delete failed' })
    } finally {
      setDeletingId(null)
    }
  }

  const clearQueue = async (target) => {
    const isPipeline = target === 'pipeline'
    const label = isPipeline ? 'DVS/AML pipeline rows' : 'ProfileSafe OCR captures'
    const phrase = confirmClearPhrase(label)
    if (!phrase) return
    const path = isPipeline ? 'clear-pipeline' : 'clear-ocr'
    try {
      setClearLoading(target)
      setClearMessage(null)
      const response = await authFetch(getLocalApiUrl(`/api/profiles/verification-queue/${path}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmPhrase: phrase }),
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || `Clear failed (HTTP ${response.status})`)
      }
      setClearMessage(result.message || `Cleared ${label}.`)
      setSelectedProfile(null)
      await loadData()
    } catch (e) {
      setClearMessage(e.message || 'Clear failed')
    } finally {
      setClearLoading(null)
    }
  }

  const runProcessDvsQueue = async () => {
    try {
      setProcessDvsLoading(true)
      setProcessDvsMessage(null)
      const response = await authFetch(getLocalApiUrl('/api/profiles/verification-queue/process-dvs'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}'
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || 'Process DVS failed')
      }
      const processed = result.processed ?? 0
      setProcessDvsMessage(
        processed === 0
          ? 'No rows in DVS Pending status. The batch button only processes pending items — use Run DVS on each row to retry failed or re-test individual records.'
          : `Processed ${processed} item(s): ${result.succeeded ?? 0} ok, ${result.failed ?? 0} failed.`
      )
      await loadData()
    } catch (e) {
      setProcessDvsMessage(e.message || 'Could not run DVS processor (check permissions).')
    } finally {
      setProcessDvsLoading(false)
    }
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Verification Queue</h1>
            <p className="text-sm text-gray-600 mt-1">
              Monitor profile onboarding and verification status in real-time
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={loading}
              className="btn-secondary"
            >
              <RefreshCcw className={`w-4 h-4 inline mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {canManage && (
            <button
              type="button"
              onClick={runProcessDvsQueue}
              disabled={processDvsLoading}
              className="btn-primary"
              title="Processes up to 10 rows in DVS Pending status only"
            >
              {processDvsLoading ? (
                <RefreshCcw className="w-4 h-4 inline mr-2 animate-spin" />
              ) : (
                <Shield className="w-4 h-4 inline mr-2" />
              )}
              Process DVS queue
            </button>
            )}
            {canManage && (
              <>
                <button
                  type="button"
                  onClick={() => clearQueue('pipeline')}
                  disabled={!!clearLoading}
                  className="btn-secondary text-red-700 border-red-200 hover:bg-red-50"
                  title="Remove all DVS/AML pipeline rows (verification_queue)"
                >
                  {clearLoading === 'pipeline' ? (
                    <RefreshCcw className="w-4 h-4 inline mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 inline mr-2" />
                  )}
                  Clear pipeline
                </button>
                <button
                  type="button"
                  onClick={() => clearQueue('ocr')}
                  disabled={!!clearLoading}
                  className="btn-secondary text-red-700 border-red-200 hover:bg-red-50"
                  title="Remove all ProfileSafe OCR-only captures"
                >
                  {clearLoading === 'ocr' ? (
                    <RefreshCcw className="w-4 h-4 inline mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 inline mr-2" />
                  )}
                  Clear OCR queue
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {clearMessage && (
        <div className="card text-sm text-gray-700 bg-amber-50 border border-amber-200">{clearMessage}</div>
      )}

      {processDvsMessage && (
        <div className="card text-sm text-gray-700 bg-slate-50 border border-slate-200">
          {processDvsMessage}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-4">
        <div className="card">
          <div className="text-sm text-gray-600">Pipeline total</div>
          <div className="text-3xl font-bold mt-2">{stats.total}</div>
        </div>
        <div className="card border-l-4 border-teal-500">
          <div className="text-sm text-gray-600">OCR captures</div>
          <div className="text-3xl font-bold mt-2 text-teal-600">{stats.ocrCaptures ?? 0}</div>
        </div>
        <div className="card border-l-4 border-blue-500">
          <div className="text-sm text-gray-600">DVS Pending</div>
          <div className="text-3xl font-bold mt-2 text-blue-600">{stats.dvsPending}</div>
        </div>
        <div className="card border-l-4 border-red-500">
          <div className="text-sm text-gray-600">DVS Failed</div>
          <div className="text-3xl font-bold mt-2 text-red-600">{stats.dvsFailed ?? 0}</div>
        </div>
        <div className="card border-l-4 border-purple-500">
          <div className="text-sm text-gray-600">AML Pending</div>
          <div className="text-3xl font-bold mt-2 text-purple-600">{stats.amlPending}</div>
        </div>
        {(stats.amlFailed ?? 0) > 0 && (
          <div className="card border-l-4 border-red-400">
            <div className="text-sm text-gray-600">AML Failed</div>
            <div className="text-3xl font-bold mt-2 text-red-500">{stats.amlFailed}</div>
          </div>
        )}
        <div className="card border-l-4 border-orange-500">
          <div className="text-sm text-gray-600">Manual Review</div>
          <div className="text-3xl font-bold mt-2 text-orange-600">{stats.manualReview}</div>
        </div>
        <div className="card border-l-4 border-green-500">
          <div className="text-sm text-gray-600">Completed</div>
          <div className="text-3xl font-bold mt-2 text-green-600">{stats.completed}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              className="select-field w-full"
              value={filters.status}
              onChange={(e) => {
                setPage(1)
                setFilters(prev => ({ ...prev, status: e.target.value }))
              }}
            >
              {FILTER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Profile Type</label>
            <select
              className="select-field w-full"
              value={filters.profileType}
              onChange={(e) => {
                setPage(1)
                setFilters(prev => ({ ...prev, profileType: e.target.value }))
              }}
            >
              <option value="ALL">All Types</option>
              <option value="MEMBER">New Members</option>
              <option value="EXISTING_MEMBER">Existing Members</option>
              <option value="VISITOR">Visitors</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                className="input-field w-full pl-10"
                placeholder="Search by name or profile ID..."
                value={filters.search}
                onChange={(e) => {
                  setPage(1)
                  setFilters(prev => ({ ...prev, search: e.target.value }))
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* DVS pipeline + OCR captures (tabbed) */}
      <div className="card">
        <div className="border-b border-gray-200 mb-4">
          <nav className="flex gap-1 -mb-px" aria-label="Verification queue lists">
            <button
              type="button"
              onClick={() => setListTab('dvs')}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                listTab === 'dvs'
                  ? 'border-psg-teal text-psg-teal'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <Shield className="w-4 h-4" />
              DVS / AML pipeline
              <span className="text-xs font-normal text-gray-500">({stats.total})</span>
            </button>
            <button
              type="button"
              onClick={() => setListTab('ocr')}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                listTab === 'ocr'
                  ? 'border-psg-teal text-psg-teal'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <ScanLine className="w-4 h-4" />
              OCR captures
              <span className="text-xs font-normal text-gray-500">({stats.ocrCaptures ?? 0})</span>
            </button>
            <button
              type="button"
              onClick={() => setListTab('nsw')}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                listTab === 'nsw'
                  ? 'border-psg-teal text-psg-teal'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <Smartphone className="w-4 h-4" />
              Digital licence
              <span className="text-xs font-normal text-gray-500">({stats.nswDdlCaptures ?? 0})</span>
            </button>
          </nav>
        </div>

        {listTab === 'dvs' ? (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Rows enter when <strong>Submit KYC (DVS)</strong> runs—after OCR confirmation—not from Scan ID alone.
            </p>
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                {error}
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="text-sm text-gray-600">
                {pagination.total === 0
                  ? 'No pipeline rows match the current filters'
                  : `Showing ${rangeFrom}–${rangeTo} of ${pagination.total.toLocaleString()} pipeline rows`}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Page size</label>
                <select
                  className="select-field text-sm py-1"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value) || 50)
                    setPage(1)
                  }}
                >
                  <option value={50}>50</option>
                  <option value={75}>75</option>
                  <option value={100}>100</option>
                </select>
                <button
                  type="button"
                  className="btn-secondary text-sm py-1 px-3"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700 whitespace-nowrap">
                  Page {pagination.page} of {totalPages}
                </span>
                <button
                  type="button"
                  className="btn-secondary text-sm py-1 px-3"
                  disabled={loading || (!pagination.hasMore && page >= totalPages)}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Profile</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">DVS</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">AML</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Documents</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Time</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map(item => (
                    <tr
                      key={item.id}
                      className={isNonActiveQueueItem(item) ? 'bg-amber-50/70 hover:bg-amber-50' : 'hover:bg-gray-50'}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium">{item.firstName} {item.lastName}</div>
                        <div className="text-xs text-gray-500 font-mono">{item.masterProfileId}</div>
                        {isNonActiveQueueItem(item) && (
                          <div className="mt-1">{getNonActiveBadge(item)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {getProfileTypeBadge(item.profileType)}
                        {item.visitorSource && (
                          <div className="text-xs text-gray-500 mt-1">
                            Source: {item.visitorSource}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(item.onboardingStatus)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                          item.dvsStatus === 'VERIFIED' ? 'bg-green-100 text-green-800' :
                          item.dvsStatus === 'FAILED' ? 'bg-red-100 text-red-800' :
                          item.dvsStatus && String(item.dvsStatus).includes('PROCESSING') ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-600'
                        }`}
                          title={dvsResponseSummary(item)}
                        >
                          {(item.dvsStatus || 'PENDING').replace('DVS_', '')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${
                          item.amlStatus === 'CLEAR' ? 'bg-green-100 text-green-800' :
                          item.amlStatus && String(item.amlStatus).includes('PROCESSING') ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {(item.amlStatus || 'PENDING').replace('AML_', '')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-400" />
                          <span>{item.documentsCount}</span>
                          {item.hasFaceprint && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                              FP
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-gray-600">
                          <Clock className="w-3 h-3" />
                          <span className="text-xs">{getTimeSince(item.updatedAt)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleViewProfile(item)}
                              className="btn-secondary text-xs"
                            >
                              <Eye className="w-3 h-3 inline mr-1" />
                              View
                            </button>
                            {canManage && canEditOrRerunDvs(item) && (
                              <button
                                type="button"
                                onClick={() => runDvsForItem(item)}
                                disabled={retryingId === item.id}
                                className="btn-primary text-xs"
                                title="Run DVS using stored OCR fields; add card number (back) in detail view if required for NSW/QLD"
                              >
                                {retryingId === item.id ? (
                                  <RefreshCcw className="w-3 h-3 inline mr-1 animate-spin" />
                                ) : (
                                  <Play className="w-3 h-3 inline mr-1" />
                                )}
                                Run DVS
                              </button>
                            )}
                            {canManage && (
                              <button
                                type="button"
                                onClick={() => softDeleteQueueItem(item)}
                                disabled={deletingId === item.id}
                                className="btn-secondary text-xs text-red-700 border-red-200 hover:bg-red-50"
                                title="Soft-delete: hide from queue, keep for audit"
                              >
                                {deletingId === item.id ? (
                                  <RefreshCcw className="w-3 h-3 inline mr-1 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3 h-3 inline mr-1" />
                                )}
                                Delete
                              </button>
                            )}
                          </div>
                          {rowActionMessage?.id === item.id && (
                            <p className={`text-xs mt-1 ${rowActionMessage.type === 'ok' ? 'text-green-700' : 'text-red-700'}`}>
                              {rowActionMessage.text}
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {items.length === 0 && !loading && !error && (
                <div className="text-center py-12 text-gray-500">
                  <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No profiles in queue</p>
                  <p className="text-sm mt-1 max-w-lg mx-auto">
                    Rows appear after someone completes <strong>Submit KYC (DVS)</strong> from Profile
                    Onboarding (not after OCR alone or final profile submit). If you expected activity, confirm KYC was
                    submitted successfully.
                  </p>
                </div>
              )}

              {loading && (
                <div className="text-center py-12">
                  <RefreshCcw className="w-8 h-8 mx-auto mb-3 text-gray-400 animate-spin" />
                  <p className="text-gray-500">Loading verification queue...</p>
                </div>
              )}
            </div>
            {pagination.total > pageSize && (
              <div className="flex items-center justify-end gap-2 mt-3">
                <button
                  type="button"
                  className="btn-secondary text-sm py-1 px-3"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {pagination.page} of {totalPages}
                </span>
                <button
                  type="button"
                  className="btn-secondary text-sm py-1 px-3"
                  disabled={loading || (!pagination.hasMore && page >= totalPages)}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : listTab === 'ocr' ? (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Each successful <strong>Scan ID</strong> (data capture) is logged here. Images are not stored—parsed fields only.
              DVS/AML appears in the pipeline tab after <strong>Submit KYC</strong>.
            </p>
            {!ocrListIncluded && (
              <p className="text-sm text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-4">
                OCR capture rows are hidden while you filter by pipeline status or profile type. Set Status and Profile Type to{' '}
                <strong>All</strong> to load recent OCR submissions (search still applies).
              </p>
            )}
            {ocrListIncluded && (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Profile</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Document</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">ProfileSafe ID</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Response</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Time</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Source</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {ocrItems.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium">
                              {item.firstName} {item.lastName}
                            </div>
                            <div className="mt-1">{getStatusBadge(item.onboardingStatus)}</div>
                          </td>
                          <td className="px-4 py-3 text-xs font-mono">
                            {item.patronSnapshot?.documentNumber || item.ocrData?.documentNumber || '—'}
                          </td>
                          <td className="px-4 py-3 text-xs font-mono text-gray-600 break-all">
                            {item.masterProfileId}
                          </td>
                          <td className="px-4 py-3 text-xs">{item.profileSafeResponseCode ?? '—'}</td>
                          <td className="px-4 py-3 text-xs text-gray-600">{getTimeSince(item.createdAt)}</td>
                          <td className="px-4 py-3 text-xs text-gray-500">{item.createdBy || '—'}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleViewProfile(item)}
                                className="btn-secondary text-xs"
                              >
                                <Eye className="w-3 h-3 inline mr-1" />
                                View
                              </button>
                              {canManage && (
                                <button
                                  type="button"
                                  onClick={() => softDeleteQueueItem(item)}
                                  disabled={deletingId === item.id}
                                  className="btn-secondary text-xs text-red-700 border-red-200 hover:bg-red-50"
                                  title="Soft-delete: hide from queue, keep for audit"
                                >
                                  {deletingId === item.id ? (
                                    <RefreshCcw className="w-3 h-3 inline mr-1 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-3 h-3 inline mr-1" />
                                  )}
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {ocrItems.length === 0 && !loading && !error && (
                  <div className="text-center py-10 text-gray-500 text-sm">
                    <ScanLine className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p>No OCR captures recorded yet.</p>
                    <p className="mt-1 max-w-md mx-auto">
                      Run <strong>Scan ID</strong> on Profile Onboarding (Documents step). Entries appear here as soon as ProfileSafe returns parsed fields.
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Each <strong>Service NSW digital licence share</strong> received on the tablet webhook is logged here when NSW POSTs to{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">/api/mobile/webhooks/nsw-ddl</code>. Parsed licence fields are stored;
              portrait is uploaded to blob storage when available. Delivery to the tablet via SSE is shown per row.
            </p>
            {!ocrListIncluded && (
              <p className="text-sm text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-4">
                Digital licence rows are hidden while you filter the pipeline by status or profile type. Set Status and Profile Type to{' '}
                <strong>All</strong> to load recent NSW captures (search still applies).
              </p>
            )}
            {ocrListIncluded && (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Patron</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Licence type</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Device</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">SSE</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Portrait</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Time</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {nswDdlItems.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium">
                              {item.licenseename || `${item.firstName || ''} ${item.lastName || ''}`.trim() || '—'}
                            </div>
                            <div className="mt-1">{getStatusBadge(item.onboardingStatus)}</div>
                          </td>
                          <td className="px-4 py-3 text-xs">{item.licenceType || '—'}</td>
                          <td className="px-4 py-3 text-xs font-mono text-gray-600 break-all">
                            {item.deviceId || '—'}
                          </td>
                          <td className="px-4 py-3 text-xs">
                            {item.sseDelivered ? (
                              <span className="text-green-700 font-medium">Delivered</span>
                            ) : (
                              <span className="text-amber-700 font-medium">Not connected</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs">
                            {item.photoRemoved
                              ? 'Removed'
                              : item.photoPresent
                                ? item.photoBlobUrl
                                  ? 'Stored'
                                  : 'Received'
                                : '—'}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600">{getTimeSince(item.createdAt)}</td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => handleViewProfile(item)}
                              className="btn-secondary text-xs"
                            >
                              <Eye className="w-3 h-3 inline mr-1" />
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {nswDdlItems.length === 0 && !loading && !error && (
                  <div className="text-center py-10 text-gray-500 text-sm">
                    <Smartphone className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p>No NSW digital licence shares recorded yet.</p>
                    <p className="mt-1 max-w-md mx-auto">
                      When a patron shares their licence via Service NSW on a registered tablet, the webhook logs the payload here and forwards it to the device over SSE.
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Auto-refresh indicator */}
      <div className="text-center text-xs text-gray-500">
        Auto-refreshes every 30 seconds
      </div>

      {/* Profile Status Detail Modal */}
      {selectedProfile && (
        <div 
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" 
          onClick={() => setSelectedProfile(null)}
        >
          <div 
            className="w-full max-w-4xl bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto" 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b z-10">
              <div className="px-6 py-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-xl font-semibold text-psg-teal">
                    {selectedProfile.rowKind === 'NSW_DDL'
                      ? (selectedProfile.licenseename ||
                          `${selectedProfile.firstName || ''} ${selectedProfile.lastName || ''}`.trim() ||
                          'NSW digital licence')
                      : `${selectedProfile.firstName || ''} ${selectedProfile.lastName || ''}`.trim()}
                  </h3>
                  {selectedProfile.profileType ? getProfileTypeBadge(selectedProfile.profileType) : null}
                  {getStatusBadge(selectedProfile.onboardingStatus)}
                  {getNonActiveBadge(selectedProfile)}
                  {detailLoading && (
                    <RefreshCcw className="w-3.5 h-3.5 text-gray-400 animate-spin" />
                  )}
                </div>
                <p className="text-sm text-gray-500 font-mono mt-1">
                  {selectedProfile.masterProfileId}
                  {selectedProfile.retryCount != null ? (
                    <span className="ml-2 font-sans text-gray-600">
                      · {selectedProfile.retryCount} DVS attempt
                      {selectedProfile.retryCount === 1 ? '' : 's'}
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {selectedProfile.createdAt
                    ? `Created ${new Date(selectedProfile.createdAt).toLocaleString()}`
                    : null}
                  {selectedProfile.updatedAt
                    ? ` · Updated ${new Date(selectedProfile.updatedAt).toLocaleString()}`
                    : null}
                </p>
                {resolveProfileSafeId(selectedProfile) && (
                  <p className="text-xs text-gray-500 font-mono mt-0.5 truncate" title="ProfileSafe profileID">
                    ProfileSafe: {formatProfileSafeIdShort(resolveProfileSafeId(selectedProfile))}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {canManage && selectedProfile.rowKind !== 'OCR' && selectedProfile.rowKind !== 'NSW_DDL' && (
                  <>
                    {canEditOrRerunDvs(selectedProfile) && (
                      <button
                        type="button"
                        onClick={() => runDvsForItem(selectedProfile)}
                        disabled={retryingId === selectedProfile.id}
                        className="btn-primary"
                      >
                        {retryingId === selectedProfile.id ? (
                          <RefreshCcw className="w-4 h-4 inline mr-2 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4 inline mr-2" />
                        )}
                        Run DVS
                      </button>
                    )}
                    {canRunAmlForItem(selectedProfile) && (
                      <button
                        type="button"
                        onClick={() => runAmlForItem(selectedProfile)}
                        disabled={runningAmlId === selectedProfile.id}
                        className="btn-secondary border-purple-300 text-purple-800"
                        title="GreenID watchlist PEP/sanctions (REST + SOAP detail)"
                      >
                        {runningAmlId === selectedProfile.id ? (
                          <RefreshCcw className="w-4 h-4 inline mr-2 animate-spin" />
                        ) : (
                          <Shield className="w-4 h-4 inline mr-2" />
                        )}
                        Run AML
                      </button>
                    )}
                  </>
                )}
                {canManage && (
                  <button
                    type="button"
                    onClick={() => softDeleteQueueItem(selectedProfile)}
                    disabled={deletingId === selectedProfile.id}
                    className="btn-secondary text-red-700 border-red-200 hover:bg-red-50"
                    title="Soft-delete: hide from queue, keep for audit"
                  >
                    {deletingId === selectedProfile.id ? (
                      <RefreshCcw className="w-4 h-4 inline mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 inline mr-2" />
                    )}
                    Delete
                  </button>
                )}
                <button
                  onClick={() => setSelectedProfile(null)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
              </div>
              {rowActionMessage?.id === selectedProfile.id && (
                <div
                  className={`px-6 py-3 border-t text-sm ${
                    rowActionMessage.type === 'ok'
                      ? 'bg-green-50 border-green-200 text-green-900'
                      : 'bg-red-50 border-red-200 text-red-900'
                  }`}
                  role="status"
                >
                  <div className="font-medium">
                    {rowActionMessage.kind === 'aml'
                      ? rowActionMessage.type === 'ok'
                        ? 'AML / PEP clear'
                        : 'AML / PEP result'
                      : rowActionMessage.type === 'ok'
                        ? 'DVS verified'
                        : 'DVS did not verify'}
                  </div>
                  <p className="mt-1 break-words">{rowActionMessage.text}</p>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {selectedProfile.rowKind === 'OCR' ? (
                <>
                  <div className="bg-teal-50 border border-teal-100 rounded-lg p-4 text-sm text-teal-900">
                    ProfileSafe OCR capture (pre-DVS). Parsed fields only; ID images and selfie are not stored on the server.
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Response code:</span>{' '}
                      <span className="font-mono">{selectedProfile.profileSafeResponseCode ?? '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Recorded:</span>{' '}
                      {selectedProfile.createdAt ? new Date(selectedProfile.createdAt).toLocaleString() : '—'}
                    </div>
                    <div>
                      <span className="text-gray-600">By:</span> {selectedProfile.createdBy ?? '—'}
                    </div>
                    <div>
                      <span className="text-gray-600">Tenant / group:</span> {selectedProfile.groupId ?? '—'}
                    </div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Parsed patron snapshot</h4>
                    <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-[420px] whitespace-pre-wrap">
                      {JSON.stringify(selectedProfile.patronSnapshot || selectedProfile.ocrData || {}, null, 2)}
                    </pre>
                  </div>
                  <p className="text-sm text-gray-600">
                    When the operator confirms OCR and runs <strong>Submit KYC</strong>, a pipeline row is created for DVS/AML.
                  </p>
                </>
              ) : selectedProfile.rowKind === 'NSW_DDL' ? (
                <>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-900">
                    Service NSW digital licence share logged from the webhook. Portrait is stored in blob storage when upload succeeds; signature presence is recorded but not stored.
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Full name:</span>{' '}
                      {selectedProfile.licenseename || `${selectedProfile.firstName || ''} ${selectedProfile.lastName || ''}`.trim() || '—'}
                    </div>
                    <div>
                      <span className="text-gray-600">Date of birth:</span>{' '}
                      {selectedProfile.dateOfBirth || selectedProfile.licenceSnapshot?.dob || '—'}
                    </div>
                    <div>
                      <span className="text-gray-600">Expiry:</span>{' '}
                      {selectedProfile.expiryDate || selectedProfile.licenceSnapshot?.expirydate || '—'}
                    </div>
                    <div>
                      <span className="text-gray-600">Licence type:</span> {selectedProfile.licenceType || '—'}
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-600">Address:</span>{' '}
                      {selectedProfile.addressLine || selectedProfile.licenceSnapshot?.address || '—'}
                    </div>
                    <div>
                      <span className="text-gray-600">Device:</span>{' '}
                      <span className="font-mono text-xs">{selectedProfile.deviceId || '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">DDR trigger:</span>{' '}
                      <span className="font-mono text-xs">{selectedProfile.ddrTriggerName || '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">SSE to tablet:</span>{' '}
                      {selectedProfile.sseDelivered ? (
                        <span className="text-green-700 font-medium">Delivered</span>
                      ) : (
                        <span className="text-amber-700 font-medium">No active listener</span>
                      )}
                    </div>
                    <div>
                      <span className="text-gray-600">Recorded:</span>{' '}
                      {selectedProfile.createdAt ? new Date(selectedProfile.createdAt).toLocaleString() : '—'}
                    </div>
                    <div>
                      <span className="text-gray-600">Tenant / group:</span> {selectedProfile.groupId ?? '—'}
                    </div>
                    <div>
                      <span className="text-gray-600">Portrait:</span>{' '}
                      {selectedProfile.photoRemoved
                        ? 'Removed per retention policy (DVS-success rules)'
                        : selectedProfile.photoPresent
                          ? selectedProfile.photoBlobUrl
                            ? selectedProfile.photoStorage === 'local' ||
                              String(selectedProfile.photoBlobUrl).startsWith('/uploads/')
                              ? 'Stored locally (from NSW base64)'
                              : selectedProfile.imageExpiresAt
                                ? `Stored — expires ${new Date(selectedProfile.imageExpiresAt).toLocaleString()}`
                                : 'Stored in blob (from NSW base64)'
                            : 'Received as base64 but storage write failed'
                          : 'Not included in payload'}
                    </div>
                    <div>
                      <span className="text-gray-600">Signature:</span>{' '}
                      {selectedProfile.signaturePresent ? 'Present in payload' : '—'}
                    </div>
                  </div>
                  {selectedProfile.photoRemoved ? (
                    <div className="border rounded-lg p-4 bg-teal-50 border-teal-100 text-sm text-teal-900">
                      Licence portrait removed as per configuration (same retention rules as DVS-verified ID
                      images).
                    </div>
                  ) : selectedProfile.photoBlobUrl || selectedProfile.photoPresent ? (
                    <NswDdlPortraitImage
                      itemId={selectedProfile.id}
                      fallbackUrl={selectedProfile.photoBlobUrl || null}
                    />
                  ) : null}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Licence snapshot</h4>
                    <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-[420px] whitespace-pre-wrap">
                      {JSON.stringify(selectedProfile.licenceSnapshot || selectedProfile.patronSnapshot || {}, null, 2)}
                    </pre>
                  </div>
                  <p className="text-sm text-gray-600">
                    When the payout completes with NSW digital KYC, a DVS/AML pipeline row may be created with DVS marked verified via Service NSW.
                  </p>
                </>
              ) : (
              <>
              {/* DVS + AML tabs */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <h4 className="font-semibold text-gray-900">Verification</h4>
                  {selectedProfile.hasFaceprint && (
                    <span className="inline-flex items-center gap-1 text-xs text-green-700">
                      <CheckCircle className="w-3.5 h-3.5" /> FacePrint captured
                    </span>
                  )}
                </div>

                {(() => {
                  const dvsTone = classifyDvsTone(selectedProfile)
                  const amlTone = classifyAmlTone(selectedProfile)
                  return (
                    <div
                      className="inline-flex w-full rounded-lg border border-psg-teal overflow-hidden bg-white shadow-sm"
                      role="tablist"
                      aria-label="Verification panels"
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={detailTab === 'dvs'}
                        onClick={() => setDetailTab('dvs')}
                        className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors cursor-pointer ${
                          detailTab === 'dvs'
                            ? 'bg-psg-teal text-white'
                            : 'bg-white text-psg-teal hover:bg-teal-50'
                        }`}
                      >
                        <span>DVS</span>
                        {verificationTabStatusPill(
                          dvsTone,
                          formatDvsStatusLabel(selectedProfile),
                          detailTab === 'dvs'
                        )}
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={detailTab === 'aml'}
                        onClick={() => setDetailTab('aml')}
                        className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold border-l border-psg-teal transition-colors cursor-pointer ${
                          detailTab === 'aml'
                            ? 'bg-psg-teal text-white'
                            : 'bg-white text-psg-teal hover:bg-teal-50'
                        }`}
                      >
                        <span>AML / PEP</span>
                        {verificationTabStatusPill(
                          amlTone,
                          formatAmlStatusLabel(selectedProfile),
                          detailTab === 'aml'
                        )}
                      </button>
                    </div>
                  )
                })()}

                {detailTab === 'dvs' ? (
                  <div role="tabpanel" className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-gray-400" />
                          <span className="font-medium">Documents / OCR images</span>
                        </div>
                        <span className="text-sm text-gray-600">
                          {selectedProfile.documentsCount || 0} on file
                        </span>
                      </div>
                      {(selectedProfile.payoutId || selectedProfile.dvsProvider) && (
                        <div className="mb-2 space-y-0.5 text-xs text-gray-600">
                          {selectedProfile.payoutId ? (
                            <p>
                              Linked payout:{' '}
                              <span className="font-mono text-gray-800">{selectedProfile.payoutId}</span>
                              {selectedProfile.kycRegistryId ? (
                                <> · KYC registry #{selectedProfile.kycRegistryId}</>
                              ) : null}
                            </p>
                          ) : null}
                          {selectedProfile.dvsProvider ? (
                            <p>
                              Provider:{' '}
                              <span className="font-medium text-gray-800">
                                {formatDvsProviderForUi(selectedProfile.dvsProvider)}
                              </span>
                            </p>
                          ) : null}
                        </div>
                      )}
                      <QueueDocumentGallery profile={selectedProfile} />
                    </div>

                    {isPayoutPipelineRow(selectedProfile) && canEditOrRerunDvs(selectedProfile) && (
                      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950">
                        Payout-sourced row — edit DVS fields below if OCR is incomplete, then Run DVS.
                      </div>
                    )}
                    <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="font-medium text-gray-900">DVS document data</div>
                        {canEditOrRerunDvs(selectedProfile) ? (
                          <button
                            type="button"
                            className="text-xs inline-flex items-center gap-1 text-psg-teal hover:underline"
                            onClick={() => setEditingDvsFields((v) => !v)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                            {editingDvsFields ? 'Done editing' : 'Edit'}
                          </button>
                        ) : (
                          <span className="text-[11px] text-gray-500">Locked after DVS success</span>
                        )}
                      </div>
                      {!editingDvsFields ? (
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs">
                          <div>
                            <dt className="text-gray-500">Document</dt>
                            <dd className="font-medium text-gray-900">
                              {dvsDocTypeConfig(dvsFields.document_type).label ||
                                dvsFields.document_type}
                              {dvsFields.state_of_issue ? ` · ${dvsFields.state_of_issue}` : ''}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">
                              {dvsDocTypeConfig(dvsFields.document_type).numberLabel}
                            </dt>
                            <dd className="font-mono text-gray-900">
                              {dvsFields.document_number || '—'}
                            </dd>
                          </div>
                          {dvsDocTypeConfig(dvsFields.document_type).showCardNumber ? (
                            <div>
                              <dt className="text-gray-500">Card number</dt>
                              <dd className="font-mono text-gray-900">
                                {dvsFields.document_card_number || '—'}
                              </dd>
                            </div>
                          ) : null}
                          <div className="sm:col-span-2">
                            <dt className="text-gray-500">Name</dt>
                            <dd className="font-medium text-gray-900">
                              {[dvsFields.given_name, dvsFields.middle_name, dvsFields.family_name]
                                .filter(Boolean)
                                .join(' ') || '—'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">Date of birth</dt>
                            <dd className="font-medium text-gray-900">
                              {formatDobDisplay(dvsFields.date_of_birth)}
                            </dd>
                          </div>
                        </dl>
                      ) : (
                      <>
                      <p className="text-xs text-gray-600 mb-3">
                        Saved when you Run DVS. Multi-word given / middle / family sent exactly as typed.
                      </p>
                      {(() => {
                        const docCfg = dvsDocTypeConfig(dvsFields.document_type)
                        return (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <label className="block text-xs sm:col-span-2">
                              <span className="text-gray-600">Document type</span>
                              <select
                                className="input-field w-full mt-1"
                                value={dvsFields.document_type}
                                onChange={(e) => {
                                  const next = e.target.value
                                  const nextCfg = dvsDocTypeConfig(next)
                                  setDvsFields((f) => ({
                                    ...f,
                                    document_type: next,
                                    country_code: nextCfg.showCountry ? f.country_code || 'AU' : 'AU',
                                    state_of_issue: nextCfg.showState
                                      ? f.state_of_issue || 'NSW'
                                      : f.state_of_issue,
                                  }))
                                }}
                              >
                                {GBG_DVS_DOCUMENT_TYPES.map((t) => (
                                  <option key={t.value} value={t.value}>
                                    {t.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                            {docCfg.showState ? (
                              <label className="block text-xs">
                                <span className="text-gray-600">State of issue</span>
                                <input
                                  className="input-field w-full mt-1"
                                  value={dvsFields.state_of_issue}
                                  onChange={(e) =>
                                    setDvsFields((f) => ({ ...f, state_of_issue: e.target.value }))
                                  }
                                  placeholder="NSW"
                                />
                              </label>
                            ) : null}
                            {docCfg.showCountry ? (
                              <label className="block text-xs">
                                <span className="text-gray-600">Country of issue</span>
                                <input
                                  className="input-field w-full mt-1"
                                  value={dvsFields.country_code}
                                  onChange={(e) =>
                                    setDvsFields((f) => ({ ...f, country_code: e.target.value }))
                                  }
                                  placeholder="AU"
                                />
                              </label>
                            ) : null}
                            <label
                              className={`block text-xs ${
                                docCfg.showState || docCfg.showCountry ? '' : 'sm:col-span-2'
                              }`}
                            >
                              <span className="text-gray-600">{docCfg.numberLabel}</span>
                              <input
                                className="input-field w-full mt-1 font-mono"
                                value={dvsFields.document_number}
                                onChange={(e) =>
                                  setDvsFields((f) => ({ ...f, document_number: e.target.value }))
                                }
                              />
                            </label>
                            {docCfg.showCardNumber ? (
                              <label className="block text-xs sm:col-span-2">
                                <span className="text-gray-600">Card number * (GBG / AU DVS)</span>
                                <input
                                  className="input-field w-full mt-1 font-mono"
                                  value={dvsFields.document_card_number}
                                  onChange={(e) => {
                                    setDvsFields((f) => ({
                                      ...f,
                                      document_card_number: e.target.value,
                                    }))
                                    setDvsCardNumber(e.target.value)
                                  }}
                                  placeholder="Separate from licence no. GBG test: 1111111111"
                                />
                              </label>
                            ) : null}
                            {docCfg.showMedicareExtra ? (
                              <>
                                <label className="block text-xs">
                                  <span className="text-gray-600">Medicare IRN / reference</span>
                                  <input
                                    className="input-field w-full mt-1 font-mono"
                                    value={dvsFields.medicare_reference}
                                    onChange={(e) =>
                                      setDvsFields((f) => ({
                                        ...f,
                                        medicare_reference: e.target.value,
                                      }))
                                    }
                                    placeholder="1"
                                  />
                                </label>
                                <label className="block text-xs">
                                  <span className="text-gray-600">Medicare colour</span>
                                  <select
                                    className="input-field w-full mt-1"
                                    value={dvsFields.medicare_colour || 'G'}
                                    onChange={(e) =>
                                      setDvsFields((f) => ({
                                        ...f,
                                        medicare_colour: e.target.value,
                                      }))
                                    }
                                  >
                                    <option value="G">Green</option>
                                    <option value="B">Blue</option>
                                    <option value="Y">Yellow</option>
                                  </select>
                                </label>
                              </>
                            ) : null}
                            {docCfg.showExpiry ? (
                              <label className="block text-xs sm:col-span-2">
                                <span className="text-gray-600">Expiry (YYYY-MM-DD)</span>
                                <input
                                  className="input-field w-full mt-1"
                                  value={dvsFields.expiry_date}
                                  onChange={(e) =>
                                    setDvsFields((f) => ({ ...f, expiry_date: e.target.value }))
                                  }
                                  placeholder="YYYY-MM-DD"
                                />
                              </label>
                            ) : null}
                            <label className="block text-xs">
                              <span className="text-gray-600">Given name (multi-word OK)</span>
                              <input
                                className="input-field w-full mt-1"
                                value={dvsFields.given_name}
                                onChange={(e) =>
                                  setDvsFields((f) => ({ ...f, given_name: e.target.value }))
                                }
                                placeholder="e.g. Thi or Thi Duc"
                              />
                            </label>
                            <label className="block text-xs">
                              <span className="text-gray-600">Middle name (optional, multi-word OK)</span>
                              <input
                                className="input-field w-full mt-1"
                                value={dvsFields.middle_name}
                                onChange={(e) =>
                                  setDvsFields((f) => ({ ...f, middle_name: e.target.value }))
                                }
                                placeholder="e.g. Duc Dung"
                              />
                            </label>
                            <label className="block text-xs sm:col-span-2">
                              <span className="text-gray-600">Family name (multi-word OK)</span>
                              <input
                                className="input-field w-full mt-1"
                                value={dvsFields.family_name}
                                onChange={(e) =>
                                  setDvsFields((f) => ({ ...f, family_name: e.target.value }))
                                }
                                placeholder="e.g. Tieu"
                              />
                            </label>
                            <p className="text-xs text-gray-500 sm:col-span-2 -mt-1">
                              Manual Run DVS sends these names exactly as typed. If GreenID reports a name
                              mismatch, try alternate splits between given / middle / family.
                            </p>
                            <label className="block text-xs sm:col-span-2">
                              <span className="text-gray-600">Date of birth (YYYY-MM-DD)</span>
                              <input
                                className="input-field w-full mt-1"
                                value={dvsFields.date_of_birth}
                                onChange={(e) =>
                                  setDvsFields((f) => ({ ...f, date_of_birth: e.target.value }))
                                }
                              />
                            </label>
                          </div>
                        )
                      })()}
                      {effectiveDvsFields(selectedProfile).ocr_source && (
                        <p className="text-xs text-teal-700 mt-2">
                          OCR source: {effectiveDvsFields(selectedProfile).ocr_source}
                        </p>
                      )}
                      </>
                      )}
                    </div>

                    {Array.isArray(selectedProfile.dvsAttempts) &&
                      selectedProfile.dvsAttempts.length > 0 && (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
                          <div className="font-medium text-gray-900 mb-2">
                            DVS attempt history ({selectedProfile.dvsAttempts.length})
                          </div>
                          <ul className="space-y-2">
                            {selectedProfile.dvsAttempts.map((a) => {
                              const req = a.request || {}
                              const nameParts = formatDvsAttemptNameParts(req)
                              const ok = String(a.outcome || '').toUpperCase() === 'SUCCESS'
                              const meta = [
                                req.date_of_birth ? `DOB ${formatDobDisplay(req.date_of_birth)}` : null,
                                req.document_number || req.document_number_masked || null,
                                req.document_card_number_masked
                                  ? `card ${req.document_card_number_masked}`
                                  : null,
                                req.state_of_issue || null,
                              ].filter(Boolean)
                              return (
                                <li
                                  key={a.id || a.attempt}
                                  className="rounded border bg-white px-3 py-2 text-xs"
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <span className="font-medium text-gray-900">
                                      Attempt {a.attempt}
                                      {a.performedAt
                                        ? ` · ${new Date(a.performedAt).toLocaleString()}`
                                        : ''}
                                    </span>
                                    <span
                                      className={`inline-flex items-center gap-1 font-semibold ${
                                        ok ? 'text-green-700' : 'text-red-700'
                                      }`}
                                    >
                                      {ok ? (
                                        <CheckCircle className="w-3.5 h-3.5" />
                                      ) : (
                                        <XCircle className="w-3.5 h-3.5" />
                                      )}
                                      {ok ? 'Verified' : 'Failed'}
                                    </span>
                                  </div>
                                  <p className="mt-1 text-gray-800">
                                    <span className="text-gray-500">Name:</span>{' '}
                                    {nameParts || '—'}
                                  </p>
                                  {meta.length > 0 ? (
                                    <p className="mt-0.5 text-gray-600">
                                      {meta.join(' · ')}
                                    </p>
                                  ) : null}
                                  {!ok && a.failureReason && (
                                    <p className="mt-1 text-red-800 break-words">
                                      {shortenDvsFailureMessage(a.failureReason)}
                                    </p>
                                  )}
                                </li>
                              )
                            })}
                          </ul>
                        </div>
                      )}

                    <DvsResponsePanel item={selectedProfile} formFields={dvsFields} />
                  </div>
                ) : (
                  <div role="tabpanel" className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-gray-400" />
                        <span className="font-medium">AML / PEP screening</span>
                      </div>
                      <span
                        className={`text-xs px-3 py-1 rounded-full font-medium border ${
                          verificationToneMeta(classifyAmlTone(selectedProfile)).chip
                        }`}
                      >
                        {formatAmlStatusLabel(selectedProfile)}
                      </span>
                    </div>
                    {classifyAmlTone(selectedProfile) === 'busy' && (
                      <div className="text-xs text-purple-600 flex items-center gap-1">
                        <RefreshCcw className="w-3 h-3 animate-spin" />
                        Screening against sanctions lists and PEP databases...
                      </div>
                    )}
                    {String(selectedProfile.amlStatus || '').toUpperCase().includes('FAIL') && (
                      <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg p-3">
                        AML screening failed or could not complete. Use <strong>Run AML</strong> to retry
                        screening.
                      </div>
                    )}
                    {String(selectedProfile.amlStatus || '').toUpperCase() === 'PENDING' && (
                      <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        {queueItemHasName(selectedProfile)
                          ? 'PEP/sanctions not run yet. Use Run AML — DVS is not required.'
                          : 'Add given and/or family name, then Run AML.'}
                      </div>
                    )}
                    {String(selectedProfile.amlStatus || '').toUpperCase() === 'PENDING_REVIEW' &&
                      String(selectedProfile.onboardingStatus || '').toUpperCase() === 'COMPLETED' && (
                        <div className="text-sm text-teal-800 bg-teal-50 border border-teal-200 rounded-lg p-3">
                          Screening completed with no PEP/sanctions hits; status label may be stale. Click Run
                          AML once to refresh to CLEAR.
                        </div>
                      )}
                    {classifyAmlTone(selectedProfile) === 'ok' && (
                      <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>No PEP / sanctions matches reported for this screening.</span>
                      </div>
                    )}
                    {(classifyAmlTone(selectedProfile) === 'err' ||
                      classifyAmlTone(selectedProfile) === 'warn' ||
                      needsManualVerify(selectedProfile)) && (
                      <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>
                          Candidate hits need <strong>Manual Verify</strong> — confirm a match or mark none of
                          these before clearing the hold.
                        </span>
                      </div>
                    )}
                    {canManage && canRunAmlForItem(selectedProfile) && (
                      <button
                        type="button"
                        onClick={() => runAmlForItem(selectedProfile)}
                        disabled={runningAmlId === selectedProfile.id}
                        className="btn-secondary border-purple-300 text-purple-800"
                      >
                        {runningAmlId === selectedProfile.id ? (
                          <RefreshCcw className="w-4 h-4 inline mr-2 animate-spin" />
                        ) : (
                          <Shield className="w-4 h-4 inline mr-2" />
                        )}
                        Run AML
                      </button>
                    )}
                    {amlVerifyLoading && (
                      <div className="text-sm text-gray-600 flex items-center gap-2">
                        <RefreshCcw className="w-4 h-4 animate-spin" />
                        Loading screening results for Manual Verify…
                      </div>
                    )}
                    {!amlVerifyLoading && amlVerifyData?.results?.length > 0 && (
                      <div className="rounded-lg border border-amber-200 bg-white p-3 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="text-sm font-semibold text-gray-900">Manual Verify</h4>
                          <span className="text-xs text-gray-500">
                            SCR-{amlVerifyData.checkId} · {amlVerifyData.resultCount} candidate
                            {amlVerifyData.resultCount === 1 ? '' : 's'}
                          </span>
                        </div>
                        <ProfileAmlMatchReviewPanel
                          results={amlVerifyData.results}
                          searchName={amlVerifyData.name || ''}
                          checkId={amlVerifyData.checkId || null}
                          initialDecision={amlVerifyData.analystDecision || null}
                          initialReason={amlVerifyData.analystReason || ''}
                          saving={amlVerifySaving}
                          onSaveReview={async ({ decision, reason, selected_match }) => {
                            if (!amlVerifyData.checkId) throw new Error('Check id missing')
                            setAmlVerifySaving(true)
                            try {
                              const resp = await authFetch(
                                getLocalApiUrl(
                                  `/api/aml/checks/${encodeURIComponent(String(amlVerifyData.checkId))}/review`
                                ),
                                {
                                  method: 'PATCH',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ decision, reason, selected_match }),
                                }
                              )
                              const data = await resp.json().catch(() => ({}))
                              if (!resp.ok || !data?.success) {
                                throw new Error(data?.error || 'Failed to save Manual Verify')
                              }
                              setAmlVerifyData((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      analystDecision: decision,
                                      analystReason: reason || '',
                                    }
                                  : prev
                              )
                              const q = data.data?.verification_queue
                              if (q && selectedProfile?.id) {
                                mergeQueueItem({
                                  id: selectedProfile.id,
                                  amlStatus: q.aml_status,
                                  onboardingStatus: q.onboarding_status,
                                })
                              }
                              setRowActionMessage({
                                id: selectedProfile.id,
                                type: decision === 'no_match' ? 'ok' : 'err',
                                kind: 'aml',
                                text:
                                  decision === 'no_match'
                                    ? 'Manual Verify saved — none of these match'
                                    : 'Manual Verify saved — match confirmed',
                              })
                            } finally {
                              setAmlVerifySaving(false)
                            }
                          }}
                        />
                      </div>
                    )}
                    {!amlVerifyLoading &&
                      needsManualVerify(selectedProfile) &&
                      amlVerifyData?.loaded &&
                      !amlVerifyData?.results?.length &&
                      !amlVerifyData?.empty && (
                        <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3">
                          Screening results could not be loaded. Try <strong>Run AML</strong> to refresh, or
                          load Manual Verify again.
                        </div>
                      )}
                    {!amlVerifyLoading &&
                      needsManualVerify(selectedProfile) &&
                      !amlVerifyData?.results?.length &&
                      !amlVerifyData?.loaded &&
                      amlMemberKeyForItem(selectedProfile) && (
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() =>
                            loadAmlVerifyForItem(
                              selectedProfile,
                              preferredAmlCheckIdForItem(selectedProfile)
                            )
                          }
                        >
                          Load Manual Verify
                        </button>
                      )}
                  </div>
                )}
              </div>

              {/* Manual Review Alert + disposition */}
              {canShowDisposition(selectedProfile) && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-orange-900 mb-1">
                        Manual Review Required
                      </h4>
                      <p className="text-sm text-orange-800">
                        DVS and/or AML did not clear automatically. For AML/PEP candidate hits, use{' '}
                        <strong>Manual Verify</strong> on the AML / PEP tab when matches are listed.
                        To finish onboarding, add remarks and <strong>Approve</strong> or <strong>Reject</strong>.
                      </p>
                    </div>
                  </div>
                  {canManage && (
                    <div>
                      <label htmlFor="vq-disposition-remarks" className="block text-sm font-medium text-orange-950 mb-1">
                        Remarks <span className="text-red-600">*</span>
                      </label>
                      <textarea
                        id="vq-disposition-remarks"
                        rows={3}
                        value={dispositionRemarks}
                        onChange={(e) => setDispositionRemarks(e.target.value)}
                        placeholder="Explain why you are approving or rejecting (required for audit)"
                        className="w-full rounded-md border border-orange-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-psg-teal focus:outline-none focus:ring-1 focus:ring-psg-teal"
                        disabled={dispositionSaving}
                      />
                      {selectedProfile.reviewRemarks && selectedProfile.reviewedAt && (
                        <p className="mt-1 text-xs text-orange-800">
                          Previous remarks by {selectedProfile.reviewedBy || 'staff'} ·{' '}
                          {new Date(selectedProfile.reviewedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <button
                  type="button"
                  onClick={() => window.location.href = `/profile-safe/search?masterProfileId=${encodeURIComponent(selectedProfile.masterProfileId || '')}`}
                  className="btn-secondary"
                >
                  View Full Profile
                </button>
                <div className="flex items-center gap-2">
                  {canManage && canShowDisposition(selectedProfile) && (
                    <>
                      <button
                        type="button"
                        className="btn-secondary text-red-600 hover:bg-red-50 disabled:opacity-50"
                        disabled={dispositionSaving || !String(dispositionRemarks || '').trim()}
                        onClick={() => submitDisposition(selectedProfile, 'reject')}
                      >
                        {dispositionSaving ? (
                          <RefreshCcw className="w-4 h-4 inline mr-2 animate-spin" />
                        ) : null}
                        Reject
                      </button>
                      <button
                        type="button"
                        className="btn-primary disabled:opacity-50"
                        disabled={dispositionSaving || !String(dispositionRemarks || '').trim()}
                        onClick={() => submitDisposition(selectedProfile, 'approve')}
                      >
                        {dispositionSaving ? (
                          <RefreshCcw className="w-4 h-4 inline mr-2 animate-spin" />
                        ) : null}
                        Approve
                      </button>
                    </>
                  )}
                </div>
              </div>
              </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
